import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Package, Plus, Search, X, Pencil, Trash2, Table2, Rows3, ClipboardList, History, Filter } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, SelectInput, TextareaInput, Switch, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, ViewModeSwitch, ActivePill } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { toolApi, toolModelApi, toolServiceRecordApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Tool, ToolModel, ToolServiceRecord } from '../../types/api';

type InvView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'inventory.viewMode';
function loadInvView(): InvView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

type ActiveFilter = 'all' | 'active' | 'inactive';

const emptyTool: Partial<Tool> = {
  toolModelId: 0, serialNumber: '', active: true, note: '',
  lastKnownIPAddress: '', lastSiteLocation: '', companyId: null, siteId: null,
};

const emptyServiceRecord: Partial<ToolServiceRecord> = {
  toolId: 0, technicianName: '', seviceDate: '', serviceWorkPerformed: '',
};

export function InventoryPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<Tool>({ fetchFn: toolApi.getAll });
  const [form, setForm] = useState<Partial<Tool>>(emptyTool);
  const [saving, setSaving] = useState(false);
  const [toolModels, setToolModels] = useState<ToolModel[]>([]);

  // Tool-form validation (was toast-based; now surfaced as field errors).
  const [modelErr, setModelErr] = useState('');
  const [serialErr, setSerialErr] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // View + filters
  const [viewMode, setViewModeRaw] = useState<InvView>(loadInvView);
  const setViewMode = (v: InvView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [modelFilter, setModelFilter] = useState<number | 'all'>('all');

  // Service-record sub-modals
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [serviceModal, setServiceModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [serviceForm, setServiceForm] = useState<Partial<ToolServiceRecord>>(emptyServiceRecord);
  const [savingService, setSavingService] = useState(false);
  const [serviceRecords, setServiceRecords] = useState<ToolServiceRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    toolModelApi.getAll({ limit: 1000 }).then((r) => setToolModels(r.entities ?? [])).catch(() => {});
  }, []);

  // ── Tool CRUD ──────────────────────────────────────────────
  const openEdit = (item: Tool) => {
    setForm({ ...item });
    setModelErr(''); setSerialErr(''); setSubmitted(false);
    crud.openEdit(item);
  };
  const openCreate = () => {
    setForm({ ...emptyTool, toolModelId: toolModels[0]?.id ?? 0 });
    setModelErr(''); setSerialErr(''); setSubmitted(false);
    crud.openCreate();
  };
  useAutoCreate(openCreate, isSuperAdmin);

  const handleSave = async () => {
    setSubmitted(true);
    let bad = false;
    if (!form.toolModelId) { setModelErr('Please select a Tool Model'); bad = true; } else { setModelErr(''); }
    if (!form.serialNumber?.trim()) { setSerialErr('Serial Number is required'); bad = true; } else { setSerialErr(''); }
    if (bad) return;
    setSaving(true);
    try {
      const result = crud.editingItem ? await toolApi.update(form) : await toolApi.create(form);
      if (result?.status === 'Failed' || result?.error) {
        crud.showToast(result?.error?.errorMessage || 'Failed to save', 'error');
        return;
      }
      const label = form.serialNumber?.trim() || 'Tool';
      crud.showToast(crud.editingItem ? 'Tool updated' : 'Tool created');
      recordActivity({ type: crud.editingItem ? 'update' : 'create', entity: 'Inventory', label, user: authUser?.username });
      crud.closeModal(); crud.load();
    } catch { crud.showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (tool: Tool) => {
    if (!confirm('Delete this tool?')) return;
    try {
      await toolApi.delete(tool.id);
      crud.showToast('Deleted');
      recordActivity({ type: 'delete', entity: 'Inventory', label: tool.serialNumber?.trim() || `Tool #${tool.id}`, user: authUser?.username });
      crud.load();
    }
    catch { crud.showToast('Failed to delete', 'error'); }
  };

  const set = (key: string, value: string | number | boolean | null) => setForm((prev) => ({ ...prev, [key]: value }));

  // ── Service records ────────────────────────────────────────
  const openServiceRecord = (tool: Tool) => {
    setSelectedTool(tool);
    setServiceForm({ ...emptyServiceRecord, toolId: tool.id });
    setServiceModal(true);
  };

  const handleSaveServiceRecord = async () => {
    if (!serviceForm.technicianName || !serviceForm.serviceWorkPerformed) return;
    setSavingService(true);
    try {
      await toolServiceRecordApi.create(serviceForm);
      crud.showToast('Service record created');
      recordActivity({ type: 'create', entity: 'Service Records', label: selectedTool?.serialNumber?.trim() || 'Tool', user: authUser?.username });
      setServiceModal(false);
    } catch { crud.showToast('Failed to save service record', 'error'); }
    finally { setSavingService(false); }
  };

  const loadServiceHistory = async (tool: Tool) => {
    setSelectedTool(tool);
    setLoadingHistory(true);
    setHistoryModal(true);
    try {
      const r = await toolServiceRecordApi.getAllByToolId(tool.id, { limit: 200 });
      setServiceRecords(r.entities ?? []);
    } catch { setServiceRecords([]); }
    finally { setLoadingHistory(false); }
  };

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  usePageHeader(
    'Tool Inventory',
    isSuperAdmin ? <Button onClick={openCreate}><Plus size={16} /> Add Tool</Button> : null,
    [isSuperAdmin, toolModels.length],
  );

  // ── Client-side filtering ──────────────────────────────────
  const filteredData = crud.data.filter((t) => {
    if (activeFilter === 'active' && !t.active) return false;
    if (activeFilter === 'inactive' && t.active) return false;
    if (modelFilter !== 'all' && t.toolModelId !== modelFilter) return false;
    return true;
  });
  const filtersActive = activeFilter !== 'all' || modelFilter !== 'all';

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const actionsW = isSuperAdmin ? 200 : 72;
  const colTemplate = compact
    ? 'minmax(160px,1.3fr) minmax(150px,1fr) minmax(120px,0.9fr) 110px minmax(180px,1.5fr)'
    : `minmax(150px,1.2fr) minmax(140px,1fr) minmax(120px,0.9fr) 110px minmax(160px,1.4fr) ${actionsW}px`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search tools…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button onClick={() => { crud.setSearchText(''); crud.setOffset(0); }} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          <ActiveSegmented value={activeFilter} onChange={setActiveFilter} />
          <ModelFilterPopover models={toolModels} value={modelFilter} onChange={setModelFilter} />

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[{ id: 'comfortable', tip: 'Comfortable view', Icon: Table2 }, { id: 'compact', tip: 'Compact view', Icon: Rows3 }]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading ? 'Loading…' : filtersActive ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total` : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Model Number" col="ToolModelName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Serial Number" col="SerialNumber" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Manufacturer Date" col="ManufacturerDate" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Active" col="Active" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Notes" col="Note" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {!compact && isSuperAdmin && <div style={{ textAlign: 'right' }}>Actions</div>}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Package size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>{filtersActive ? 'No tools match this filter' : 'No tools found'}</div>
              <div style={{ fontSize: 12 }}>{filtersActive ? 'Try a different filter.' : isSuperAdmin ? 'Add a new tool to get started.' : 'No tools to display.'}</div>
            </div>
          ) : (
            <div>
              {filteredData.map((t, i) => (
                <ToolRow
                  key={t.id ?? i}
                  tool={t}
                  compact={compact}
                  isSuperAdmin={isSuperAdmin}
                  colTemplate={colTemplate}
                  isLast={i === filteredData.length - 1}
                  onHistory={() => loadServiceHistory(t)}
                  onService={() => openServiceRecord(t)}
                  onEdit={() => openEdit(t)}
                  onDelete={() => handleDelete(t)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> tools
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Create / Edit Tool modal — Active switch lives in the header */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit Tool' : 'New Tool'}
        icon={<Package size={20} />}
        headerRight={<Switch checked={!!form.active} onChange={(v) => set('active', v)} label={form.active ? 'Active' : 'Inactive'} />}
        footer={
          <>
            <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add Tool'}
            </PrimaryButton>
          </>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 210px', gap: 20, alignItems: 'start' }}>
          {/* Left: editable fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Field label="Model Number" required error={submitted ? modelErr : undefined}>
              <SelectInput
                value={form.toolModelId ?? ''}
                error={submitted && !!modelErr}
                onChange={(e) => { set('toolModelId', Number(e.target.value)); if (submitted && e.target.value) setModelErr(''); }}
              >
                <option value="">— Select Model —</option>
                {toolModels.map((m) => <option key={m.id} value={m.id}>{m.toolModelName}</option>)}
              </SelectInput>
            </Field>
            <Field label="Serial Number" required error={submitted ? serialErr : undefined}>
              <TextInput
                value={form.serialNumber ?? ''}
                placeholder="e.g. PRG-001234"
                error={submitted && !!serialErr}
                onChange={(e) => { set('serialNumber', e.target.value); if (submitted && e.target.value.trim()) setSerialErr(''); }}
              />
            </Field>
            <Field label="Manufacturer Date">
              <TextInput
                type="date"
                value={form.manufacturerDate?.split('T')[0] ?? ''}
                onChange={(e) => set('manufacturerDate', e.target.value)}
              />
            </Field>
            <Field label="Note">
              <TextareaInput
                rows={3}
                value={form.note ?? ''}
                placeholder="Optional notes about this tool…"
                onChange={(e) => set('note', e.target.value)}
              />
            </Field>
          </div>

          {/* Right: read-only information panel */}
          <InfoPanel form={form} />
        </div>
      </FormModal>

      {/* Create Service Record modal */}
      <FormModal
        open={serviceModal}
        onClose={() => setServiceModal(false)}
        title="Create Service Record"
        icon={<ClipboardList size={20} />}
        footer={
          <>
            <GhostButton onClick={() => setServiceModal(false)} disabled={savingService}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSaveServiceRecord} disabled={savingService || !serviceForm.technicianName || !serviceForm.serviceWorkPerformed}>
              {savingService ? 'Saving…' : 'Save Record'}
            </PrimaryButton>
          </>
        }
      >
        {selectedTool && <ToolContext tool={selectedTool} />}
        <Field label="Technician Name" required>
          <TextInput
            value={serviceForm.technicianName ?? ''}
            placeholder="e.g. Jane Smith"
            onChange={(e) => setServiceForm((p) => ({ ...p, technicianName: e.target.value }))}
          />
        </Field>
        <Field label="Date">
          <TextInput
            type="date"
            value={serviceForm.seviceDate?.split('T')[0] ?? ''}
            onChange={(e) => setServiceForm((p) => ({ ...p, seviceDate: e.target.value }))}
          />
        </Field>
        <Field label="Service Work Performed" required>
          <TextareaInput
            rows={4}
            value={serviceForm.serviceWorkPerformed ?? ''}
            placeholder="Describe the service performed…"
            onChange={(e) => setServiceForm((p) => ({ ...p, serviceWorkPerformed: e.target.value }))}
          />
        </Field>
      </FormModal>

      {/* Service Record History modal */}
      <FormModal
        open={historyModal}
        onClose={() => setHistoryModal(false)}
        title="Service Record History"
        icon={<History size={20} />}
        footer={<GhostButton onClick={() => setHistoryModal(false)}>Close</GhostButton>}
      >
        {selectedTool && <ToolContext tool={selectedTool} />}
        {loadingHistory ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
        ) : serviceRecords.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <ClipboardList size={28} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
            <div style={{ fontSize: 13.5 }}>No service records found.</div>
          </div>
        ) : (
          <HistoryList records={serviceRecords} />
        )}
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

/** Secondary row-action button (History / Service record) — solid dark grey, distinct from Edit/Delete. */
function GhostIcon({ title, onClick, size = 36, children }: { title: string; onClick: () => void; size?: number; children: ReactNode }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 8, border: `1px solid ${hover ? '#374151' : '#4b5563'}`, cursor: 'pointer',
        background: hover ? '#374151' : '#4b5563',
        color: '#fff',
        transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.12)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function ToolActions({
  isSuperAdmin, size, onHistory, onService, onEdit, onDelete,
}: {
  isSuperAdmin: boolean; size: number;
  onHistory: () => void; onService: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const ico = size === 30 ? 15 : 17;
  return (
    <>
      <GhostIcon title="Service record history" onClick={onHistory} size={size}><History size={ico} /></GhostIcon>
      {isSuperAdmin && <GhostIcon title="Create service record" onClick={onService} size={size}><ClipboardList size={ico} /></GhostIcon>}
      {isSuperAdmin && <IconButton title="Edit" onClick={onEdit} size={size}><Pencil size={ico} /></IconButton>}
      {isSuperAdmin && <IconButton title="Delete" onClick={onDelete} hoverDanger size={size}><Trash2 size={ico} /></IconButton>}
    </>
  );
}

function ToolRow({
  tool: t, compact, isSuperAdmin, colTemplate, isLast, onHistory, onService, onEdit, onDelete,
}: {
  tool: Tool; compact: boolean; isSuperAdmin: boolean; colTemplate: string; isLast: boolean;
  onHistory: () => void; onService: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');
  const wrap = { overflowWrap: 'anywhere' as const, wordBreak: 'break-word' as const };
  const mfgDate = t.manufacturerDate ? new Date(t.manufacturerDate).toLocaleDateString() : '—';

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      <Cell style={{ ...wrap, fontWeight: 500 }}>{t.toolModel?.toolModelName ?? '—'}</Cell>
      <Cell style={{ ...wrap, fontFamily: 'var(--font-mono)', fontSize: 13 }}>{t.serialNumber || '—'}</Cell>
      <Cell style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-mid)' }}>{mfgDate}</Cell>
      <div><ActivePill active={!!t.active} /></div>
      <Cell style={{ ...wrap, color: 'var(--color-text-mid)' }}>{t.note || '—'}</Cell>

      {!compact && isSuperAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <ToolActions isSuperAdmin={isSuperAdmin} size={36} onHistory={onHistory} onService={onService} onEdit={onEdit} onDelete={onDelete} />
        </div>
      )}
      {!compact && !isSuperAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <GhostIcon title="Service record history" onClick={onHistory}><History size={17} /></GhostIcon>
        </div>
      )}

      {compact && (
        <div
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            display: 'flex', alignItems: 'center', gap: 6, paddingRight: 14, paddingLeft: 56,
            background: `linear-gradient(90deg, transparent 0%, ${rowBg} 32%, ${rowBg} 100%)`,
            opacity: hover ? 1 : 0,
            pointerEvents: hover ? 'auto' : 'none',
            transition: 'opacity 150ms',
            zIndex: 5,
          }}
        >
          <ToolActions isSuperAdmin={isSuperAdmin} size={30} onHistory={onHistory} onService={onService} onEdit={onEdit} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
}

/** Read-only telemetry panel shown beside the tool form (matches the old Information card). */
function InfoPanel({ form }: { form: Partial<Tool> }) {
  const rows: [string, ReactNode][] = [
    ['Last Site Location', form.lastSiteLocation || '—'],
    ['Total Hours Drilling', (form.totalHoursDrilling ?? 0).toFixed(1)],
    ['Total Hours Circulating', (form.totalHoursCirculating ?? 0).toFixed(1)],
    ['Total Hours Below Rotary', (form.totalHoursBelowRotary ?? 0).toFixed(1)],
  ];
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--color-panel-alt)' }}>
      <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--color-text-mid)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
        Information
      </div>
      <div>
        {rows.map(([k, v], i) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, padding: '10px 14px', borderBottom: i < rows.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-mid)', lineHeight: 1.35 }}>{k}</span>
            <span style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 600, fontFamily: 'var(--font-mono)', textAlign: 'right', whiteSpace: 'nowrap' }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Small "which tool" context chip for the service-record sub-modals. */
function ToolContext({ tool }: { tool: Tool }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)' }}>
      <Package size={15} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: 'var(--color-text-mid)' }}>
        {tool.toolModel?.toolModelName ?? 'Tool'} · <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-text)', fontWeight: 500 }}>{tool.serialNumber || `#${tool.id}`}</span>
      </span>
    </div>
  );
}

function HistoryList({ records }: { records: ToolServiceRecord[] }) {
  const tpl = 'minmax(120px,1fr) 110px minmax(200px,2fr)';
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: tpl, gap: 12, padding: '11px 14px', background: 'var(--color-panel-alt)', borderBottom: '1px solid var(--color-border)', fontSize: 12.5, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--color-text-mid)', fontWeight: 600 }}>
        <div>Technician</div>
        <div>Date</div>
        <div>Service Work Performed</div>
      </div>
      {records.map((rec, i) => (
        <div key={rec.id ?? i} style={{ display: 'grid', gridTemplateColumns: tpl, gap: 12, padding: '12px 14px', borderBottom: i < records.length - 1 ? '1px solid var(--color-border)' : 'none', fontSize: 14, alignItems: 'baseline' }}>
          <div style={{ fontWeight: 600, color: 'var(--color-text)', overflowWrap: 'anywhere' }}>{rec.technicianName || '—'}</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-mid)', whiteSpace: 'nowrap' }}>{rec.seviceDate ? new Date(rec.seviceDate).toLocaleDateString() : '—'}</div>
          <div style={{ color: 'var(--color-text-mid)', overflowWrap: 'anywhere', lineHeight: 1.45 }}>{rec.serviceWorkPerformed || '—'}</div>
        </div>
      ))}
    </div>
  );
}

function ActiveSegmented({ value, onChange }: { value: ActiveFilter; onChange: (v: ActiveFilter) => void }) {
  const opts: { v: ActiveFilter; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'active', label: 'Active', dot: 'var(--color-good)' },
    { v: 'inactive', label: 'Inactive', dot: 'var(--color-text-dim)' },
  ];
  return (
    <div style={{ display: 'inline-flex', padding: 3, background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, gap: 2 }}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', border: 'none', borderRadius: 6,
              fontSize: 12.5, fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && <span style={{ width: 6, height: 6, borderRadius: 3, background: o.dot, display: 'inline-block' }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function ModelFilterPopover({ models, value, onChange }: { models: ToolModel[]; value: number | 'all'; onChange: (v: number | 'all') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = value !== 'all';
  const selectedName = active ? (models.find((m) => m.id === value)?.toolModelName ?? 'Model') : '';

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Filter by model"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 12px', borderRadius: 8,
          border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
          background: active ? 'rgba(87,154,59,0.08)' : 'var(--color-panel-alt)',
          color: active ? 'var(--color-accent)' : 'var(--color-text-mid)',
          fontSize: 12.5, fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer', transition: 'all 120ms',
        }}
      >
        <Filter size={14} />
        {active ? selectedName : 'Model'}
        {active && (
          <span
            onClick={(e) => { e.stopPropagation(); onChange('all'); }}
            aria-label="Clear model filter"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 8, marginLeft: 2 }}
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 50, width: 260, maxHeight: 320,
            background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>
            Tool Model
          </div>
          <div style={{ overflowY: 'auto', padding: 6 }}>
            <ModelOption label="All models" selected={value === 'all'} onClick={() => { onChange('all'); setOpen(false); }} />
            {models.map((m) => (
              <ModelOption key={m.id} label={m.toolModelName} selected={value === m.id} onClick={() => { onChange(m.id); setOpen(false); }} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModelOption({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 7, border: 'none',
        cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)',
        background: selected ? 'rgba(87,154,59,0.08)' : hover ? 'var(--color-panel-alt)' : 'transparent',
        color: selected ? 'var(--color-accent)' : 'var(--color-text)',
        fontWeight: selected ? 600 : 400,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 3, background: selected ? 'var(--color-accent)' : 'var(--color-border)', display: 'inline-block', flexShrink: 0 }} />
      <span style={{ overflowWrap: 'anywhere' }}>{label}</span>
    </button>
  );
}
